/**
 * 에셋 단위 프레임 추출기
 *
 * 목적:
 * - 에셋 ID별로 프레임을 한 번만 추출하여 중복 추출 방지
 * - n초 간격으로 프레임을 생성하여 메모리 효율 최적화
 * - 추출된 프레임을 ImageBitmap으로 저장하여 빠른 렌더링
 *
 * 사용 방법:
 * - AssetFramesContext와 함께 사용
 * - 에셋이 캐싱되면 자동으로 프레임 추출 시작
 */

import { WEBCODECS_TIMESCALE } from '@remotion/media-parser';
import { extractFramesOnWebWorker } from '@remotion/webcodecs/worker';

// Canvas 기반 프레임 추출 사용 여부 (WebWorker 실패 시 대체)
const USE_CANVAS_EXTRACTION = true;

// 프레임 추출 간격 (초)
export const DEFAULT_FRAME_INTERVAL_SECONDS = 1.5;

// 프레임 썸네일 높이 (px)
export const FRAME_THUMBNAIL_HEIGHT = 46;

// 추출 상태
export type ExtractionStatus = 'idle' | 'extracting' | 'done' | 'error';

// 추출된 프레임 데이터
export interface ExtractedFrame {
  timestampInSeconds: number;
  imageBitmap: ImageBitmap;
}

// 에셋별 프레임 데이터
export interface AssetFrameData {
  assetId: string;
  status: ExtractionStatus;
  frames: ExtractedFrame[];
  error?: Error;
  durationInSeconds: number;
  aspectRatio: number;
}

// 우선순위 추출 범위
export interface PriorityRange {
  start: number; // 초
  end: number; // 초
}

// 추출 옵션
export interface ExtractFramesOptions {
  assetId: string;
  blobUrl: string;
  durationInSeconds: number;
  aspectRatio: number;
  intervalInSeconds?: number;
  priorityRanges?: PriorityRange[]; // 우선 추출할 시간 범위 (타임라인에 보이는 구간)
  onProgress?: (extracted: number, total: number) => void;
  onFrame?: (frame: ExtractedFrame) => void;
  onComplete?: (frames: ExtractedFrame[]) => void;
  onError?: (error: Error) => void;
  signal?: AbortSignal;
}

/**
 * n초 간격으로 추출할 타임스탬프 목록 생성
 * priorityRanges가 제공되면 해당 범위의 타임스탬프를 우선 배치
 */
export function generateTimestampsToExtract(
  durationInSeconds: number,
  intervalInSeconds: number = DEFAULT_FRAME_INTERVAL_SECONDS,
  priorityRanges?: PriorityRange[],
): number[] {
  const timestamps: number[] = [];
  let currentTime = 0;

  while (currentTime < durationInSeconds) {
    timestamps.push(currentTime);
    currentTime += intervalInSeconds;
  }

  // 마지막 프레임이 duration보다 작으면 마지막 프레임도 추가
  if (timestamps.length > 0) {
    const lastTimestamp = timestamps[timestamps.length - 1] ?? 0;
    if (lastTimestamp < durationInSeconds - 0.5) {
      timestamps.push(Math.max(0, durationInSeconds - 0.1));
    }
  }

  // 우선순위 범위가 없으면 그대로 반환
  if (!priorityRanges || priorityRanges.length === 0) {
    return timestamps;
  }

  // 우선순위 범위에 속하는 타임스탬프와 나머지 분리
  const priorityTimestamps: number[] = [];
  const normalTimestamps: number[] = [];

  for (const timestamp of timestamps) {
    const isInPriorityRange = priorityRanges.some(
      (range) =>
        timestamp >= range.start - intervalInSeconds &&
        timestamp <= range.end + intervalInSeconds,
    );

    if (isInPriorityRange) {
      priorityTimestamps.push(timestamp);
    } else {
      normalTimestamps.push(timestamp);
    }
  }

  // 우선순위 타임스탬프를 먼저, 나머지를 뒤에 배치
  return [...priorityTimestamps, ...normalTimestamps];
}

/**
 * Canvas 기반 프레임 추출 (WebWorker 대체)
 * HTMLVideoElement를 사용하여 특정 시간의 프레임을 Canvas로 캡처
 */
async function extractFrameWithCanvas(
  video: HTMLVideoElement,
  timestampInSeconds: number,
  targetHeight: number,
): Promise<ImageBitmap> {
  return new Promise((resolve, reject) => {
    // 타임아웃 설정 (5초)
    const timeout = setTimeout(() => {
      video.removeEventListener('seeked', seekHandler);
      reject(new Error(`Seek timeout at ${timestampInSeconds}s`));
    }, 5000);

    const captureFrame = async () => {
      try {
        // Canvas에 프레임 그리기
        const aspectRatio = video.videoWidth / video.videoHeight;
        const width = Math.round(targetHeight * aspectRatio);

        const canvas = new OffscreenCanvas(width, targetHeight);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }

        ctx.drawImage(video, 0, 0, width, targetHeight);

        // ImageBitmap으로 변환
        const imageBitmap = await createImageBitmap(canvas);
        resolve(imageBitmap);
      } catch (err) {
        reject(err);
      }
    };

    const seekHandler = async () => {
      clearTimeout(timeout);
      video.removeEventListener('seeked', seekHandler);
      await captureFrame();
    };

    video.addEventListener('seeked', seekHandler);

    // 현재 위치와 목표 위치가 같으면 seeked 이벤트가 발생하지 않으므로
    // 직접 캡처 실행
    const currentTime = video.currentTime;
    const targetTime = timestampInSeconds;

    if (Math.abs(currentTime - targetTime) < 0.01) {
      // 이미 해당 위치에 있으면 바로 캡처
      clearTimeout(timeout);
      video.removeEventListener('seeked', seekHandler);
      captureFrame();
    } else {
      // 지정된 시간으로 이동
      video.currentTime = timestampInSeconds;
    }
  });
}

/**
 * Canvas 기반 프레임 추출 메인 함수
 */
async function extractFramesWithCanvas(
  options: ExtractFramesOptions,
): Promise<ExtractedFrame[]> {
  const {
    blobUrl,
    durationInSeconds,
    intervalInSeconds = DEFAULT_FRAME_INTERVAL_SECONDS,
    priorityRanges,
    onProgress,
    onFrame,
    onComplete,
    onError,
    signal,
  } = options;

  const timestampsToExtract = generateTimestampsToExtract(
    durationInSeconds,
    intervalInSeconds,
    priorityRanges,
  );

  const extractedFrames: ExtractedFrame[] = [];

  // 비디오 요소 생성
  const video = document.createElement('video');
  video.preload = 'auto';
  video.muted = true;
  // blob URL에서는 crossOrigin 설정 불필요 (tainted canvas 문제 방지)

  try {
    // 비디오 로드 대기
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Video load timeout'));
      }, 30000);

      video.onloadeddata = () => {
        clearTimeout(timeout);
        resolve();
      };

      video.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('Video load error'));
      };

      video.src = blobUrl;
    });

    // 각 타임스탬프에서 프레임 추출
    for (let i = 0; i < timestampsToExtract.length; i++) {
      if (signal?.aborted) {
        break;
      }

      const timestamp = timestampsToExtract[i];
      if (timestamp === undefined) continue;

      try {
        const imageBitmap = await extractFrameWithCanvas(
          video,
          Math.min(timestamp, durationInSeconds - 0.1),
          FRAME_THUMBNAIL_HEIGHT,
        );

        const extractedFrame: ExtractedFrame = {
          timestampInSeconds: timestamp,
          imageBitmap,
        };

        extractedFrames.push(extractedFrame);
        onFrame?.(extractedFrame);
        onProgress?.(extractedFrames.length, timestampsToExtract.length);
      } catch (frameErr) {
        // 프레임 추출 실패는 무시하고 계속 진행
        console.warn(
          `[AssetFrameExtractor] Failed to extract frame at ${timestamp}s`,
        );
      }
    }
    onComplete?.(extractedFrames);
    return extractedFrames;
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    console.error('[AssetFrameExtractor] Canvas extraction failed:', err);
    onError?.(err);
    throw err;
  } finally {
    // 비디오 요소 정리
    video.src = '';
    video.load();
  }
}

/**
 * 에셋에서 프레임 추출
 * 참고: blob URL 유효성 검사는 AssetFramesContext에서 수행하므로 여기서는 생략
 */
export async function extractFramesFromAsset(
  options: ExtractFramesOptions,
): Promise<ExtractedFrame[]> {
  // Canvas 기반 추출 사용 시 바로 해당 함수 호출
  if (USE_CANVAS_EXTRACTION) {
    return extractFramesWithCanvas(options);
  }

  // 이하 WebWorker 기반 추출 코드 (현재 미사용, 향후 사용을 위해 유지)
  const {
    blobUrl,
    durationInSeconds,
    intervalInSeconds = DEFAULT_FRAME_INTERVAL_SECONDS,
    onProgress,
    onFrame,
    onComplete,
    onError,
    signal,
  } = options;

  const timestampsToExtract = generateTimestampsToExtract(
    durationInSeconds,
    intervalInSeconds,
  );

  console.log('[AssetFrameExtractor] Timestamps to extract:', {
    count: timestampsToExtract.length,
    first: timestampsToExtract[0],
    last: timestampsToExtract[timestampsToExtract.length - 1],
  });

  const totalFrames = timestampsToExtract.length;
  const extractedFrames: ExtractedFrame[] = [];
  let extractedCount = 0;

  // 이미 추출된 타임스탬프 추적
  const extractedTimestamps = new Set<number>();

  try {
    console.log('[AssetFrameExtractor] Calling extractFramesOnWebWorker...', {
      totalTimestamps: timestampsToExtract.length,
      firstTimestamp: timestampsToExtract[0],
      lastTimestamp: timestampsToExtract[timestampsToExtract.length - 1],
      blobUrl: blobUrl.substring(0, 100),
    });

    // 비디오 요소로 blob URL 워밍업 (메타데이터 로드)
    // 이렇게 하면 브라우저가 blob URL을 비디오로 인식하고 캐시함
    try {
      console.log(
        '[AssetFrameExtractor] Warming up blob URL with video element...',
      );
      await new Promise<void>((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'auto';
        video.muted = true;

        const timeout = setTimeout(() => {
          video.src = '';
          reject(new Error('Video metadata load timeout'));
        }, 10000);

        video.onloadedmetadata = () => {
          clearTimeout(timeout);
          console.log('[AssetFrameExtractor] Video metadata loaded:', {
            duration: video.duration,
            videoWidth: video.videoWidth,
            videoHeight: video.videoHeight,
          });
          video.src = '';
          resolve();
        };

        video.onerror = (e) => {
          clearTimeout(timeout);
          video.src = '';
          reject(new Error(`Video load error: ${e}`));
        };

        video.src = blobUrl;
      });
    } catch (warmupErr) {
      console.warn('[AssetFrameExtractor] Blob URL warmup failed:', warmupErr);
      // 워밍업 실패해도 계속 진행
    }

    await extractFramesOnWebWorker({
      acknowledgeRemotionLicense: true,
      src: blobUrl,
      timestampsInSeconds: () => {
        // 아직 추출되지 않은 타임스탬프만 반환
        // V1처럼 duration 이하로 제한
        const remaining = timestampsToExtract
          .filter((t) => !extractedTimestamps.has(Math.round(t * 1000)))
          .map((t) => Math.min(t, durationInSeconds - 0.1));

        // 테스트: 매우 적은 수의 타임스탬프만 반환
        // 0초에서 프레임 하나만 추출 시도
        const BATCH_SIZE = 3;
        const batch = remaining.slice(0, BATCH_SIZE);

        console.log('[AssetFrameExtractor] timestampsInSeconds called', {
          remaining: remaining.length,
          batchSize: batch.length,
          actualBatch: batch,
          durationInSeconds,
        });

        // 빈 배열 반환 시 추출 종료
        if (batch.length === 0) {
          console.log('[AssetFrameExtractor] No more timestamps to extract');
        }

        return batch;
      },
      onFrame: (videoFrame) => {
        console.log(
          '[AssetFrameExtractor] onFrame called, timestamp:',
          videoFrame.timestamp,
        );
        const timestampInSeconds = videoFrame.timestamp / WEBCODECS_TIMESCALE;

        // 가장 가까운 목표 타임스탬프 찾기
        const closestTimestamp = timestampsToExtract.reduce((prev, curr) =>
          Math.abs(curr - timestampInSeconds) <
          Math.abs(prev - timestampInSeconds)
            ? curr
            : prev,
        );

        // 이미 추출된 타임스탬프는 스킵
        const timestampKey = Math.round(closestTimestamp * 1000);
        if (extractedTimestamps.has(timestampKey)) {
          videoFrame.close();
          return;
        }

        extractedTimestamps.add(timestampKey);

        // ImageBitmap으로 변환 (비동기지만 fire-and-forget으로 처리)
        // VideoFrame을 즉시 복사하여 사용 (close 전에)
        createImageBitmap(videoFrame, {
          resizeHeight: FRAME_THUMBNAIL_HEIGHT,
          resizeQuality: 'low',
        })
          .then((imageBitmap) => {
            const extractedFrame: ExtractedFrame = {
              timestampInSeconds: closestTimestamp,
              imageBitmap,
            };

            extractedFrames.push(extractedFrame);
            extractedCount++;

            // 콜백 호출
            onFrame?.(extractedFrame);
            onProgress?.(extractedCount, totalFrames);
          })
          .catch((err) => {
            console.error('Failed to process frame:', err);
          });

        // VideoFrame 닫기 (createImageBitmap이 데이터를 복사하므로 바로 닫아도 됨)
        videoFrame.close();
      },
      signal,
    });

    console.log('[AssetFrameExtractor] extractFramesOnWebWorker completed', {
      extractedFramesCount: extractedFrames.length,
      extractedTimestampsSize: extractedTimestamps.size,
    });

    // 타임스탬프 순서로 정렬
    extractedFrames.sort((a, b) => a.timestampInSeconds - b.timestampInSeconds);

    onComplete?.(extractedFrames);
    return extractedFrames;
  } catch (error) {
    if (signal?.aborted) {
      // 취소된 경우는 에러로 처리하지 않음
      return extractedFrames;
    }

    const err = error instanceof Error ? error : new Error(String(error));
    onError?.(err);
    throw err;
  }
}

/**
 * 특정 시간에 해당하는 프레임 찾기
 */
export function findFrameAtTime(
  frames: ExtractedFrame[],
  timeInSeconds: number,
): ExtractedFrame | null {
  if (frames.length === 0) return null;

  // 이진 검색으로 가장 가까운 프레임 찾기
  let left = 0;
  let right = frames.length - 1;

  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    const midFrame = frames[mid];
    if (midFrame && midFrame.timestampInSeconds < timeInSeconds) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  // 현재 위치와 이전 위치 중 더 가까운 것 선택
  const current = frames[left];
  const previous = frames[left - 1];

  if (!previous) return current ?? null;
  if (!current) return previous ?? null;

  const currentDiff = Math.abs(current.timestampInSeconds - timeInSeconds);
  const previousDiff = Math.abs(previous.timestampInSeconds - timeInSeconds);

  return currentDiff < previousDiff ? current : previous;
}

/**
 * 특정 시간 범위의 프레임들 찾기
 */
export function findFramesInRange(
  frames: ExtractedFrame[],
  startTimeInSeconds: number,
  endTimeInSeconds: number,
): ExtractedFrame[] {
  return frames.filter(
    (frame) =>
      frame.timestampInSeconds >= startTimeInSeconds - 1 &&
      frame.timestampInSeconds <= endTimeInSeconds + 1,
  );
}

/**
 * ImageBitmap 정리 (메모리 해제)
 */
export function cleanupFrames(frames: ExtractedFrame[]): void {
  frames.forEach((frame) => {
    frame.imageBitmap.close();
  });
}
